import { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'

import { ChartSkeleton } from '@digital-twins/deck-ui/components'
import { CategoricalFigure } from './views/CategoricalFigure'
import { ChoiceBarsFigure } from './views/ChoiceBarsFigure'
import { DensityHistogramFigure } from './views/DensityHistogramFigure'
import { DivergingLikertFigure } from './views/DivergingLikertFigure'
import { HeatGridFigure } from './views/HeatGridFigure'
import { RankingBarsFigure } from './views/RankingBarsFigure'

import { RankingChartLabels } from '@digital-twins/deck-ui/components'
import { StudyFigureNote } from '@digital-twins/shared-types'
import { FigureEntry, FigureFailure } from './figureBatcher'
import { FigurePanel, FigureViewProps } from './views/figureViewShared'

/**
 * The survey tab's figure adapters, one per plot kind the figure service draws
 * for a question (plots plan D3). Every entry is a SEAM: the kind's step
 * (I ranking, J choice, L stacked/grouped, M histogram, P likert, Q heat)
 * replaces its adapter with the library's plot component; nothing else here
 * changes. A kind with no entry draws the no-data state.
 */
export const FIGURE_VIEWS: Record<string, ComponentType<FigureViewProps>> = {
  'choice-bars': ChoiceBarsFigure,
  'ranking-bars': RankingBarsFigure,
  'stacked-100-bars': CategoricalFigure,
  'grouped-bars': CategoricalFigure,
  'density-histogram': DensityHistogramFigure,
  'diverging-likert': DivergingLikertFigure,
  'heat-grid': HeatGridFigure,
}

interface QuestionFigureViewProps {
  entry: FigureEntry
  /** True when a cut is active: the cut's groups are drawn, not Total. */
  isCut: boolean
  colors: Array<string>
  onRetry: () => void
  rankingLabels?: Partial<RankingChartLabels>
  /**
   * The service drew no figure for the view the reader picked (Wave 7 added
   * kinds): say so in the card's place, controls kept.
   */
  isNotCharted?: boolean
  /** The number the card header already prints, when it prints one. */
  visibleDenominator?: number | null
}

/** The drawn panels: Total alone, or every drawn cut group that has a plot. */
export function figurePanels(entry: FigureEntry, isCut: boolean): Array<FigurePanel> {
  if (entry.status !== 'ready' || !entry.question) {
    return []
  }
  const question = entry.question
  return entry.groups
    .filter((group) => (isCut ? group.kind === 'cut' : group.kind === 'total'))
    .filter((group) => group.drawn && question.plots[group.id] != null)
    .map((group) => ({ group, plot: question.plots[group.id] }))
}

/** Each failure's copy; only a plain failure offers a retry (a 400 is final). */
const FAILURE_COPY_KEYS = {
  stale_report: 'studyReport.retryErrors.reportReplaced',
  rejected: 'studyReport.questionCard.figureCutUnavailable',
  failed: 'studyReport.questionCard.figureLoadFailed',
} as const satisfies Record<FigureFailure, string>

const FAILURE_TEST_IDS: Record<FigureFailure, string> = {
  stale_report: 'question-figure-stale',
  rejected: 'question-figure-rejected',
  failed: 'question-figure-error',
}

/** The service's typed notes a reader needs, in the viewer's language. */
const NOTE_COPY_KEYS = {
  question_invalid: 'studyReport.questionCard.figureInvalid',
  segments_dropped: 'studyReport.questionCard.figureSegmentsDropped',
} as const

type NotedCode = keyof typeof NOTE_COPY_KEYS

function isNotedCode(code: StudyFigureNote['code']): code is NotedCode {
  return code in NOTE_COPY_KEYS
}

/** Groups the service left undrawn because the filtered sample or cut group is under 10. */
function tooFewGroups(entry: FigureEntry, isCut: boolean) {
  if (entry.status !== 'ready') {
    return []
  }
  return entry.groups.filter((group) => {
    if (group.drawn || group.reason !== 'too_few') {
      return false
    }
    if (isCut) {
      return group.kind === 'cut'
    }
    return group.kind === 'total'
  })
}

interface TooFewCutLineProps {
  label: string
}

/** A cut group's name with the too-few sentence beside it, the same row as a low-base mark. */
function TooFewCutLine(props: TooFewCutLineProps) {
  const { label } = props

  const { t } = useTranslation('app')

  return (
    <div className="mt-4 flex items-baseline gap-2 text-sm font-semibold text-foreground">
      <span>{label}</span>
      <span className="text-xs font-normal text-muted-foreground" data-testid="question-figure-too-few">
        {t('studyReport.questionCard.tooFewToChart')}
      </span>
    </div>
  )
}

interface FigureNotesProps {
  notes: Array<StudyFigureNote>
}

function FigureNotes(props: FigureNotesProps) {
  const { notes } = props

  const { t } = useTranslation('app')

  const codes = Array.from(new Set(notes.map((note) => note.code))).filter(isNotedCode)
  // Notes the service words itself, in the request's UI locale (a pair's base, folded groups).
  const worded = notes.filter(
    (note) => (note.code === 'paired_base' || note.code === 'cut_groups_folded') && note.label,
  )
  if (codes.length === 0 && worded.length === 0) {
    return null
  }
  return (
    <div className="mt-3 space-y-1">
      {codes.map((code) => (
        <p key={code} className="text-xs text-muted-foreground" data-testid={`question-figure-note-${code}`}>
          {t(NOTE_COPY_KEYS[code])}
        </p>
      ))}
      {worded.map((note) => (
        <p
          key={`${note.code}-${note.questionId ?? ''}`}
          className="text-xs text-muted-foreground"
          data-testid={`question-figure-note-${note.code}`}
        >
          {note.label}
        </p>
      ))}
    </div>
  )
}

interface FigureEntryStatusProps {
  entry: FigureEntry
  onRetry: () => void
  /** The service drew no figure for the view the reader picked. */
  isNotCharted?: boolean
  /** Draw the loading skeleton (a figure card); a theme card keeps its words instead. */
  showsLoading?: boolean
}

/**
 * A figure entry's states other than drawn (loading, failure with its copy and
 * retry, "not charted for this group"), shared by every figure card and the
 * theme cards. Nothing when the entry is drawn.
 */
export function FigureEntryStatus(props: FigureEntryStatusProps) {
  const { entry, onRetry, isNotCharted = false, showsLoading = true } = props

  const { t } = useTranslation('app')

  if (entry.status === 'loading') {
    return showsLoading ? (
      <div data-testid="question-figure-loading">
        <ChartSkeleton />
      </div>
    ) : null
  }
  if (entry.status === 'error') {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/20 p-6 text-sm text-muted-foreground min-h-48"
        data-testid={FAILURE_TEST_IDS[entry.failure]}
      >
        <span>{t(FAILURE_COPY_KEYS[entry.failure])}</span>
        {entry.failure === 'failed' && (
          <button
            type="button"
            className="rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
            onClick={onRetry}
            data-testid="question-figure-retry"
          >
            {t('studyReport.retryGeneration')}
          </button>
        )}
      </div>
    )
  }
  if (isNotCharted) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
        data-testid="question-figure-not-charted"
      >
        <span className="text-sm">{t('studyReport.questionCard.notChartedForGroup')}</span>
      </div>
    )
  }
  return null
}

export function QuestionFigureView(props: QuestionFigureViewProps) {
  const { entry, isCut, colors, onRetry, rankingLabels, isNotCharted = false, visibleDenominator = null } = props

  const { t, i18n } = useTranslation('app')

  if (entry.status !== 'ready' || (isNotCharted && !entry.question)) {
    return <FigureEntryStatus entry={entry} onRetry={onRetry} isNotCharted={isNotCharted} />
  }

  const panels = figurePanels(entry, isCut)
  const View = entry.question ? FIGURE_VIEWS[entry.question.kind] : undefined
  const tooFew = tooFewGroups(entry, isCut)
  // A cut whose every group is under the floor still names each group as too small.
  const isCutAllTooFew = isCut && panels.length === 0 && tooFew.length > 0
  if (!entry.question || !View || (panels.length === 0 && !isCutAllTooFew)) {
    const isInvalid = entry.notes.some((note) => note.code === 'question_invalid')
    if (!isInvalid && !isCut && tooFew.length > 0) {
      return (
        <div
          className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
          data-testid="question-figure-too-few"
        >
          <span className="text-sm">{t('studyReport.questionCard.tooFewToChart')}</span>
        </div>
      )
    }
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
        data-testid={isInvalid ? 'question-figure-invalid' : 'question-figure-empty'}
      >
        <span className="text-sm">
          {isInvalid ? t('studyReport.questionCard.figureInvalid') : t('studyReport.numericChart.noData')}
        </span>
      </div>
    )
  }
  const undrawn = isCut
    ? entry.groups.filter((group) => group.kind === 'cut' && !group.drawn && group.reason !== 'too_few')
    : []

  return (
    <div data-testid="question-figure">
      {panels.length > 0 && (
        <View
          question={entry.question}
          panels={panels}
          isCut={isCut}
          colors={colors}
          locale={i18n.language}
          rankingLabels={rankingLabels}
          visibleDenominator={visibleDenominator}
          otherToggleLabels={{ expand: (count: number) => t('studyReport.plotOther.expand', { count }), collapse: t('studyReport.plotOther.collapse') }}
        />
      )}
      {tooFew.map((group) => (
        <TooFewCutLine key={group.id} label={group.label ?? group.sourceId ?? ''} />
      ))}
      {undrawn.length > 0 && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid="question-figure-undrawn-groups">
          {undrawn.map((group) => `${group.label ?? group.sourceId ?? ''}: ${t('studyReport.numericChart.noData')}`).join(' · ')}
        </p>
      )}
      <FigureNotes notes={entry.notes} />
    </div>
  )
}
